import { Ref, forwardRef } from "react"
import { Formik, FormikHelpers, FormikProps } from "formik"
import * as yup from "yup"
import { StandardFormikErrorScrollWrapper } from "../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../currency/currencyLib"
import { CreateUpdateExtraServiceFormContent } from "./CreateUpdateExtraServiceFormContent"
import {
    CreateUpdateExtraServiceFormValues,
    ExternalServiceEntry,
    ExtraServiceType,
    ExtraServicePricingType,
    ExtraServiceActionType,
} from "../extraServicesTypes"
import { useDateTime } from "../../../../localization/useDateTime"
import { DaysOfWeek } from "../../../merchant/menuPlanning/menuPlanningTypes"
import { useMerchant } from "../../../../merchants/merchantHooks"

type CreateUpdateProductFormProps = Readonly<{
    initialValues?: CreateUpdateExtraServiceFormValues | null
    onSubmit: (formData: ExternalServiceEntry) => void
}>
type FormikRef = Ref<FormikProps<CreateUpdateExtraServiceFormValues>>

const defaultInitialValues: CreateUpdateExtraServiceFormValues = {
    name: "",
    productType: "",
    action: "" as any, //  need for formik to work properly after clear selection
    productGroupCode: undefined,
    description: "",
    isActive: true,
    isMeetingTimeRequired: false,
    pricingType: ExtraServicePricingType.PER_SEAT,
    serviceType: ExtraServiceType.CLEANUP_FEE,
    serviceTime: {
        weekDays: [],
        timeInterval: undefined,
    },
    minimumPrice: 0,
    maximumPrice: 0,
    pricePerSeat: 0,
    minimumPriceCurrency: "DKK",
    maximumPriceCurrency: "DKK",
    pricePerSeatCurrency: "DKK",
}

export const CreateUpdateExtraServiceForm = forwardRef(
    ({ initialValues, onSubmit }: CreateUpdateProductFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const dateTimeFactory = useDateTime()
        const { merchantId } = useMerchant()

        const validationSchema = yup.object({
            name: yup.string().required(l10n.getString("required-field")),
            description: yup
                .string()
                .max(
                    500,
                    l10n.getString("extra-services-create-service-description-field-max-length", { maxLength: 500 }),
                ),
            isActive: yup.boolean(),
            productType: yup.string(),
            serviceType: yup.string(),
            serviceTime: yup.object({
                weekDays: yup.array().of(yup.string().oneOf(Object.values(DaysOfWeek))),
                timeInterval: yup.object({
                    start: yup.date(),
                    end: yup
                        .date()
                        .min(
                            yup.ref("start"),
                            l10n.getString("merchant-delivery-location-form-end-time-picker-min-error-label"),
                        ),
                }),
            }),
            isMeetingTimeRequired: yup.boolean(),
            action: yup.mixed<ExtraServiceActionType>().oneOf(Object.values(ExtraServiceActionType)).notRequired(),
        })

        function handleSubmit(
            values: CreateUpdateExtraServiceFormValues,
            _helpers: FormikHelpers<CreateUpdateExtraServiceFormValues>,
        ) {
            const {
                isActive,
                action,
                isMeetingTimeRequired,
                serviceTime,
                productGroupCode,
                name,
                description,
                serviceType,
                pricingType,
            } = values

            const productGroupCodeBody = productGroupCode
                ? {
                      productGroupCode: {
                          id: productGroupCode.id,
                      },
                  }
                : {}

            const serviceTimeModified =
                serviceTime.timeInterval && serviceTime.timeInterval.end
                    ? {
                          weekDays: serviceTime.weekDays,
                          timeInterval: {
                              start: dateTimeFactory.toHourMinute(serviceTime.timeInterval.start),
                              end: dateTimeFactory.toHourMinute(serviceTime.timeInterval.end),
                          },
                      }
                    : {}

            const commonFields = {
                serviceType,
                productType: "EXTRA_SERVICE" as const,
                name,
                description,
                isActive,
                action,
                isMeetingTimeRequired,
                ...productGroupCodeBody,
                merchant: { id: merchantId },
                serviceTime: serviceTimeModified,
            }

            const modifyData: ExternalServiceEntry =
                pricingType === ExtraServicePricingType.PER_SEAT
                    ? {
                          ...commonFields,
                          pricingType: ExtraServicePricingType.PER_SEAT,
                          minimumPrice: toMonetaryAmount(
                              toDineroLossy(values.minimumPrice!, toCurrency(values.minimumPriceCurrency)),
                          ),
                          maximumPrice: toMonetaryAmount(
                              toDineroLossy(values.maximumPrice!, toCurrency(values.maximumPriceCurrency)),
                          ),
                          pricePerSeat: toMonetaryAmount(
                              toDineroLossy(values.pricePerSeat!, toCurrency(values.pricePerSeatCurrency)),
                          ),
                      }
                    : {
                          ...commonFields,
                          pricingType: ExtraServicePricingType.FLAT_FEE,
                          price: toMonetaryAmount(
                              toDineroLossy(values.defaultPrice!, toCurrency(values.defaultCurrency)),
                          ),
                      }

            onSubmit({
                ...modifyData,
            })
        }

        return (
            <>
                <Formik<CreateUpdateExtraServiceFormValues>
                    enableReinitialize
                    initialValues={initialValues ?? defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    <StandardFormikErrorScrollWrapper<CreateUpdateExtraServiceFormValues>
                        children={<CreateUpdateExtraServiceFormContent />}
                    />
                </Formik>
            </>
        )
    },
)
